import { useState } from 'react';
import { Download } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { formatINR } from '@vj/shared';
import { api, downloadFile } from '../../lib/api.js';
import { Button } from '../../components/ui/Button.jsx';

export default function OutstandingBalancesPage() {
  const { data: rows, isLoading } = useQuery({
    queryKey: ['reports', 'outstanding-balances'],
    queryFn: () => api.get('/reports/outstanding-balances'),
  });
  const [downloading, setDownloading] = useState(false);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadFile('/reports/exports/outstanding-balances', 'outstanding-balances.csv');
    } finally {
      setDownloading(false);
    }
  };

  const total = (rows ?? []).reduce((acc, r) => acc + Number(r.totalBalance), 0);

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-semibold tracking-tight">Outstanding balances</h1>
          <p className="mt-1 text-sm text-muted">Customer-wise receivables — every issued invoice with a balance still due.</p>
        </div>
        <Button variant="secondary" size="sm" loading={downloading} onClick={handleDownload}>
          <Download size={16} aria-hidden="true" /> CSV
        </Button>
      </div>

      {rows?.length ? (
        <div className="card p-5">
          <p className="text-xs uppercase tracking-wide text-muted">Total outstanding</p>
          <p className="tabular mt-1 text-2xl font-semibold text-warning">{formatINR(total)}</p>
        </div>
      ) : null}

      <div className="card table-scroll">
        <table className="w-full min-w-[560px] text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted">
              <th className="px-4 py-3">Customer</th>
              <th className="px-4 py-3 text-right">Invoices</th>
              <th className="px-4 py-3 text-right">Balance due</th>
              <th className="px-4 py-3">Last invoice</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr><td colSpan={4} className="px-4 py-10 text-center text-muted">Loading…</td></tr>
            ) : !rows?.length ? (
              <tr><td colSpan={4} className="px-4 py-10 text-center text-muted">Nothing outstanding — every issued invoice is fully paid.</td></tr>
            ) : (
              rows.map((r, i) => (
                <tr key={`${r.customerId ?? 'walkin'}-${r.customerName}-${i}`} className="tabular border-b border-border last:border-0">
                  <td className="px-4 py-3 font-medium">
                    {r.customerName}
                    {r.customerMobile ? <div className="text-xs font-normal text-muted">{r.customerMobile}</div> : null}
                  </td>
                  <td className="px-4 py-3 text-right">{r.invoiceCount}</td>
                  <td className="px-4 py-3 text-right font-semibold text-warning">{formatINR(r.totalBalance)}</td>
                  <td className="px-4 py-3 text-muted">{r.lastInvoiceDate}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
